import type {
  WebhookDelivery,
  WebhookDeliveryStatus,
  WebhookSubscription,
} from '@servicetim/shared';
import { WEBHOOK_DELIVERY_STATUSES } from '@servicetim/shared';
import { useRetryWebhookDelivery, useWebhookDeliveries } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  describe,
  pagedTableProps,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  toast,
} from '@servicetim/ui/components';
import { RotateCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { SimpleSelect } from '../projects/ui/simple-select';

/*
 * Tiny local copies of `tenant/common/format.ts#formatDateTime` and
 * `tenant/common/server-errors.ts#problemMessage`: importing the real ones would create a
 * `feature-tenant <-> feature-webhooks` circular chunk (`webhooks-table.tsx`, in `feature-tenant`,
 * already imports this component), which crashes at runtime with a temporal-dead-zone
 * `ReferenceError` (Vite/Rollup manual chunking is per top-level `features/*` folder — see
 * `vite.config.ts`). Keep these in sync with `tenant/common/format.ts` by eye if they change.
 */
const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });
function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : dateTime.format(date);
}
function problemMessage(error: unknown): string {
  const view = describe(error);
  const field = view.fieldErrors[0];
  const head = view.detail ?? view.title;
  return field ? `${head}: ${field.message}` : head;
}

const STATUS_VARIANT: Record<
  WebhookDeliveryStatus,
  'success' | 'destructive' | 'warning' | 'secondary'
> = {
  delivered: 'success',
  dead: 'destructive',
  failed: 'warning',
  pending: 'secondary',
};

const STATUS_OPTIONS = WEBHOOK_DELIVERY_STATUSES.map((s) => ({ value: s, label: s }));

/** Delivery attempt log for one webhook subscription, with a manual retry (S-08-07). */
export function DeliveriesDrawer({
  subscription,
  onOpenChange,
}: {
  subscription: WebhookSubscription | null;
  onOpenChange: (open: boolean) => void;
}) {
  const open = subscription !== null;
  const [status, setStatus] = useState<WebhookDeliveryStatus | undefined>(undefined);
  const deliveries = useWebhookDeliveries(subscription?.id, { status });
  const retry = useRetryWebhookDelivery(subscription?.id);

  const columns = useMemo<ColumnDef<WebhookDelivery, unknown>[]>(
    () => [
      {
        accessorKey: 'attempt',
        header: 'Attempt',
        cell: ({ row }) => row.original.attempt,
      },
      {
        accessorKey: 'eventType',
        header: 'Event',
        cell: ({ row }) => <code className="font-mono text-xs">{row.original.eventType}</code>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={STATUS_VARIANT[row.original.status]}>{row.original.status}</Badge>
        ),
      },
      {
        accessorKey: 'responseStatus',
        header: 'Response',
        cell: ({ row }) => row.original.responseStatus ?? '—',
      },
      {
        accessorKey: 'nextAttemptAt',
        header: 'Next attempt',
        cell: ({ row }) =>
          row.original.nextAttemptAt ? formatDateTime(row.original.nextAttemptAt) : '—',
      },
      {
        accessorKey: 'error',
        header: 'Error',
        cell: ({ row }) =>
          row.original.error ? (
            <span
              className="block max-w-56 truncate text-red-700 dark:text-red-300 text-xs"
              title={row.original.error}
            >
              {row.original.error}
            </span>
          ) : (
            '—'
          ),
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.status === 'failed' || row.original.status === 'dead' ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={retry.isPending}
              onClick={async () => {
                try {
                  await retry.mutateAsync(row.original.id);
                  toast.success('Retry attempted');
                } catch (error) {
                  toast.error(problemMessage(error));
                }
              }}
            >
              <RotateCw /> Retry
            </Button>
          ) : null,
      },
    ],
    [retry.isPending, retry.mutateAsync],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>
            {subscription ? `Deliveries · ${subscription.name}` : 'Deliveries'}
          </SheetTitle>
          <SheetDescription>
            One row per attempt; failed and dead deliveries can be retried manually.
          </SheetDescription>
        </SheetHeader>
        <div className="max-w-xs">
          <SimpleSelect
            aria-label="Filter by status"
            value={status}
            emptyLabel="Every status"
            options={STATUS_OPTIONS}
            onChange={(next) => setStatus(next as WebhookDeliveryStatus | undefined)}
          />
        </div>
        <DataTable
          columns={columns}
          {...pagedTableProps(deliveries)}
          getRowId={(d) => d.id}
          caption="Webhook deliveries"
          emptyTitle="No deliveries yet"
          emptyDescription="Attempts appear here once a matching event fires."
        />
      </SheetContent>
    </Sheet>
  );
}
